/**
 * Task-mode streaming client (D14).
 *
 * The route name (plus an optional rung offset) is the ONLY routing input —
 * the server stamps the SSOT model/ladder/effort at the door
 * (taskModeRewrite) and streams OpenRouter-shaped chunks back. Slice 7
 * (2026-09-15) retired the legacy cross-provider transports
 * (runOpenRouterRequest / runOpenRouterStream) and the openRouterProxy
 * client URL with the deploy-skew fallbacks that consumed them; the file
 * keeps its name so imports stay stable.
 *
 * Uses codename "Prism" in user-facing messages — never mention "OpenRouter".
 */

import type { TokenUsageMetadata } from '../components/GlobalChatSystem/types';
import { knownAccountHint } from './accountHint';
import { SERVER_METER_HEADERS } from './proxyMeter';
import { proxyAuthHeaders } from './proxyAuth';


// anthropicProxy URL — the task-mode door (D14 slice 5b-ii). Same override
// convention as the other anthropicProxy clients.
function getAnthropicProxyUrl(): string {
  const override = (process.env.REACT_APP_ANTHROPIC_PROXY_URL || '').trim();
  if (override) return override;
  const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID || 'tensile-tenure-453409-k3';
  return `https://us-central1-${projectId}.cloudfunctions.net/anthropicProxy`;
}

// Retry configuration
const MAX_RETRIES = 3;
const INITIAL_RETRY_DELAY_MS = 1000;

// Helper to delay for exponential backoff
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export interface OpenRouterMessage {
  role: 'system' | 'user' | 'assistant';
  content: string | OpenRouterContentBlock[];
}

export type OpenRouterContentBlock =
  | { type: 'text'; text: string }
  | { type: 'image_url'; image_url: { url: string } }
  | { type: 'file_url'; file_url: { url: string; mime_type?: string; filename?: string } };

export interface OpenRouterAttachment {
  type: 'image_url' | 'file_url';
  url: string;
  mimeType?: string;
  filename?: string;
}


export interface OpenRouterResult {
  text: string;
  raw?: unknown;
  tokenUsage?: TokenUsageMetadata;
}

function withAttachments(
  messages: OpenRouterMessage[],
  attachments?: OpenRouterAttachment[]
): OpenRouterMessage[] {
  if (!attachments || attachments.length === 0) return messages;

  const attachmentBlocks: OpenRouterContentBlock[] = attachments.map((attachment) => {
    if (attachment.type === 'image_url') {
      return {
        type: 'image_url',
        image_url: { url: attachment.url },
      };
    }

    return {
      type: 'file_url',
      file_url: {
        url: attachment.url,
        ...(attachment.mimeType ? { mime_type: attachment.mimeType } : {}),
        ...(attachment.filename ? { filename: attachment.filename } : {}),
      },
    };
  });

  return messages.map((message) => {
    if (message.role !== 'user') return message;
    const baseBlocks: OpenRouterContentBlock[] = typeof message.content === 'string'
      ? [{ type: 'text', text: message.content }]
      : [...message.content];
    return {
      ...message,
      content: [...baseBlocks, ...attachmentBlocks],
    };
  });
}

/**
 * Check if an error is an auth error (non-retryable)
 */
function isAuthError(err: any): boolean {
  const AUTH_CODES = ['API_KEY_NOT_CONFIGURED', 'OPENROUTER_AUTH_FAILED'];
  return AUTH_CODES.includes(err?.code) || err?.status === 401;
}

/**
 * Check if an error is retryable (transient)
 */
function isRetryable(err: any): boolean {
  if (isAuthError(err)) return false;
  const status = err?.status;
  return status === 500 || status === 502 || status === 503 || status === 504 ||
    status === 429 || status === 408;
}

/**
 * D14 slice 5b-ii: the task-mode streaming transport — the route name (and,
 * for ladder descent, a rung offset) is the ONLY routing input; the server
 * stamps the SSOT model/ladder/effort (taskModeRewrite) and its stream is the
 * same OpenRouter chunk format this client already parses (Door A is a
 * passthrough — probe-verified 2026-08-26). A rung offset past the ladder end
 * answers 400 TASK_LADDER_EXHAUSTED, which walking callers treat as the end
 * of descent, never as an error to retry.
 */
export interface TaskStreamParams {
  /** SSOT task route name (e.g. 'translation'). */
  route: string;
  /** Ladder rung offset for quality-judgment descent (default 0). */
  rung?: number;
  messages: OpenRouterMessage[];
  maxTokens?: number;
  temperature?: number;
  signal?: AbortSignal;
  /** Multimodal attachments appended to user messages (OpenRouter-form
   *  blocks — Door A's converter passes non-Anthropic blocks through). */
  attachments?: OpenRouterAttachment[];
  onChunk?: (chunk: string, accumulated: string) => void;
  onComplete?: (fullText: string, tokenUsage?: TokenUsageMetadata) => void;
  onError?: (error: Error) => void;
  onActivity?: () => void;
  /** Ledger-row summary for the server-metered expense — every call through
   *  this client is billed by the proxy's server meter; absent ⇒ the proxy
   *  derives one from the task route. */
  meterSummary?: string;
}

export async function runTaskStream(params: TaskStreamParams): Promise<OpenRouterResult> {
  const rung = params.rung && params.rung > 0 ? params.rung : 0;
  const body = {
    mode: 'task',
    route: params.route,
    ...(rung > 0 ? { rung } : {}),
    messages: withAttachments(params.messages, params.attachments),
    stream: true,
    stream_options: { include_usage: true },
    ...(params.maxTokens ? { max_tokens: params.maxTokens } : {}),
    ...(params.temperature !== undefined ? { temperature: params.temperature } : {}),
    ...(params.meterSummary ? { meterSummary: params.meterSummary } : {}),
  };

  return streamThroughProxy({
    proxyUrl: getAnthropicProxyUrl(),
    body,
    extraHeaders: { 'X-Prest0n-Task': params.route },
    displayModel: `task:${params.route}${rung > 0 ? `#${rung}` : ''}`,
    signal: params.signal,
    onChunk: params.onChunk,
    onComplete: params.onComplete,
    onError: params.onError,
    onActivity: params.onActivity,
  });
}

/**
 * Shared connect-retry + SSE parse core for both proxy doors. The stream is
 * OpenAI-chunk-shaped on both (Door A passes OpenRouter chunks through).
 * tokenUsage.model prefers the SERVED id the chunks carry (R17-F doctrine:
 * served-model truth beats the requested label) and falls back to the
 * display label.
 */
async function streamThroughProxy(args: {
  proxyUrl: string;
  body: Record<string, unknown>;
  extraHeaders?: Record<string, string>;
  displayModel: string;
  signal?: AbortSignal;
  onChunk?: (chunk: string, accumulated: string) => void;
  onComplete?: (fullText: string, tokenUsage?: TokenUsageMetadata) => void;
  onError?: (error: Error) => void;
  onActivity?: () => void;
}): Promise<OpenRouterResult> {
  const { proxyUrl, body } = args;
  const params = args;
  const startTime = Date.now();

  let response: Response | null = null;
  let lastError: any = null;

  // Retry connection
  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    if (attempt > 0) {
      const backoffMs = INITIAL_RETRY_DELAY_MS * Math.pow(2, attempt - 1);
      console.log(`[OpenRouter Stream] Retry ${attempt}/${MAX_RETRIES} after ${backoffMs}ms for ${args.displayModel}`);
      await delay(backoffMs);
    }

    try {
      const accountHint = knownAccountHint();
      response = await fetch(proxyUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
          // Billed by the proxy (server meter) — see ./proxyMeter.
          ...SERVER_METER_HEADERS,
          ...(accountHint ? { 'X-Account-Id': accountHint } : {}),
          // Who is calling — the signed-in user's ID token (security slice 6, ./proxyAuth).
          ...(await proxyAuthHeaders()),
          ...(args.extraHeaders || {}),
        },
        body: JSON.stringify(body),
        signal: params.signal,
      });

      if (response.ok || !isRetryable({ status: response.status })) break;

      if (attempt < MAX_RETRIES - 1) continue;
    } catch (fetchErr: any) {
      lastError = fetchErr;
      if (attempt === MAX_RETRIES - 1) {
        params.onError?.(fetchErr);
        throw fetchErr;
      }
    }
  }

  if (!response) {
    const err = lastError || new Error('Failed to connect to Prism after retries');
    params.onError?.(err);
    throw err;
  }

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    const message = (data?.error as any)?.message || data?.message || `Prism request failed (${response.status})`;
    const code = (data?.error as any)?.code || data?.code || `HTTP_${response.status}`;
    const err: any = new Error(message);
    err.status = response.status;
    err.code = code;
    params.onError?.(err);
    throw err;
  }

  const reader = response.body?.getReader();
  if (!reader) {
    const err = new Error('No response body for Prism streaming');
    params.onError?.(err);
    throw err;
  }

  const decoder = new TextDecoder('utf-8');
  let accumulated = '';
  let buffer = '';
  let lastUsage: any = null;
  let lastFinishReason: string | null = null;
  let lastServedModel: string | null = null;

  // Per-chunk timeout — 30s is sufficient for OpenRouter (faster than Perplexity)
  const CHUNK_TIMEOUT_MS = 60000;

  try {
    while (true) {
      if (params.signal?.aborted) {
        console.log('[OpenRouter Stream] Abort signal detected - stopping stream');
        break;
      }

      const readPromise = reader.read();
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Prism stream chunk timeout after 60 seconds')), CHUNK_TIMEOUT_MS);
      });

      let readResult: ReadableStreamReadResult<Uint8Array>;
      try {
        readResult = await Promise.race([readPromise, timeoutPromise]);
      } catch (timeoutError) {
        console.error('[OpenRouter Stream] Stream chunk timeout');
        throw timeoutError;
      }

      const { done, value } = readResult;
      if (done) break;

      // Bytes arrived. This is the ONLY liveness signal that stays true during
      // high-effort reasoning and keep-alive traffic — see onActivity.
      params.onActivity?.();

      buffer += decoder.decode(value, { stream: true });

      // Process complete SSE events from buffer
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed || trimmed === 'data: [DONE]') continue;
        if (!trimmed.startsWith('data: ')) continue;

        try {
          const jsonStr = trimmed.slice(6);
          const parsed = JSON.parse(jsonStr);

          // OpenAI-compatible delta
          const delta = parsed?.choices?.[0]?.delta?.content;
          if (delta) {
            accumulated += delta;
            params.onChunk?.(delta, accumulated);
          }

          const finishReason = parsed?.choices?.[0]?.finish_reason;
          if (finishReason) lastFinishReason = finishReason;

          if (parsed?.usage) lastUsage = parsed.usage;
          if (parsed?.model) lastServedModel = String(parsed.model);
        } catch {
          // Ignore malformed JSON chunks
        }
      }
    }

    // Process remaining buffer
    if (buffer.trim() && buffer.trim().startsWith('data: ') && buffer.trim() !== 'data: [DONE]') {
      try {
        const jsonStr = buffer.trim().slice(6);
        const parsed = JSON.parse(jsonStr);
        const delta = parsed?.choices?.[0]?.delta?.content;
        if (delta) {
          accumulated += delta;
          params.onChunk?.(delta, accumulated);
        }
        if (parsed?.usage) lastUsage = parsed.usage;
        if (parsed?.choices?.[0]?.finish_reason) lastFinishReason = parsed.choices[0].finish_reason;
      } catch {
        // Ignore
      }
    }
  } finally {
    reader.releaseLock();
  }

  const responseTimeMs = Date.now() - startTime;
  const finalText = accumulated.trim();

  const tokenUsage: TokenUsageMetadata = {
    inputTokens: lastUsage?.prompt_tokens,
    outputTokens: lastUsage?.completion_tokens,
    totalTokens: lastUsage?.total_tokens,
    model: lastServedModel || args.displayModel,
    provider: 'openrouter',
    responseTimeMs,
    timestamp: new Date(),
    finishReason: lastFinishReason || undefined,
  };

  console.log(`[OpenRouter Stream] Completed ${args.displayModel} in ${responseTimeMs}ms, ${finalText.length} chars (served: ${lastServedModel || 'n/a'})`);
  params.onComplete?.(finalText, tokenUsage);

  return { text: finalText, tokenUsage };
}
